import { Layout, Menu } from 'antd';
import { FC, useEffect, useState } from 'react';
import styles from './index.module.scss';
import './index.scss';
import { SidebarProps } from './types';
import {
	ApprovalIcon,
	ConfigurationsSvg,
	DashboardSvg,
	EmployeeSvg,
	NotificationSvgSettings,
	PayrollSettings,
	PayrollSvg,
	ReportSvg,
	RoleSvg,
	TimeActivitiesSvg,
	UsersSvg,
	CoaIcon,
	DashboardSelectedSvg,
	EmployeeSelectedSvg,
	LeaveRequestSelectedSvg,
	LeaveRequestSvg,
	TimeActivitiesSelectedSvg,
	PayrollSelectedSvg,
	ReportSelectedSvg,
	UsersSelectedSvg,
	RoleSelectedSvg,
	PayrollSettingsSelected,
	NotificationSvgSettingsSelected,
	ApprovalIconSelected,
	CoaIconSelected,
} from 'utils/svgs';
import { DownOutlined, UpOutlined } from '@ant-design/icons';

const getKeyWiseIcons = {
	users: <UsersSvg />,
	roles: <RoleSvg />,
	'payroll-settings': <PayrollSettings />,
	approval: <ApprovalIcon />,
	notification: <NotificationSvgSettings />,
	'payroll-wizard': <ConfigurationsSvg />,
	dashboard: <DashboardSvg />,
	employees: <EmployeeSvg />,
	'time-activities-main': <TimeActivitiesSvg />,
	shifts: '',
	'daily-attendance': '',
	'monthly-summary': '',
	'time-sheets': '',
	'time-off': <LeaveRequestSvg />,
	'run-payroll-main': <PayrollSvg />,
	'mid-month': '',
	'loan-request': '',
	'calculate-statutory-components': '',
	'run-payroll': '',
	payments: '',
	reports: <ReportSvg />,
	coa: <CoaIcon />,
};

const getSelectedKeyWiseIcons = {
	users: <UsersSelectedSvg />,
	roles: <RoleSelectedSvg />,
	'payroll-settings': <PayrollSettingsSelected />,
	approval: <ApprovalIconSelected />,
	notification: <NotificationSvgSettingsSelected />,
	'payroll-wizard': <ConfigurationsSvg isSelected={true} />,
	dashboard: <DashboardSelectedSvg />,
	employees: <EmployeeSelectedSvg />,
	'time-activities-main': <TimeActivitiesSelectedSvg />,
	shifts: '',
	'daily-attendance': '',
	'monthly-summary': '',
	'time-sheets': '',
	'time-off': <LeaveRequestSelectedSvg />,
	'run-payroll-main': <PayrollSelectedSvg />,
	'mid-month': '',
	'loan-request': '',
	'calculate-statutory': '',
	'run-payroll': '',
	'payroll-overview': '',
	payments: '',
	reports: <ReportSelectedSvg />,
	coa: <CoaIconSelected />,
};

// Global sidebar
const Sidebar: FC<SidebarProps> = (props) => {
	const { Sider } = Layout;
	const { handleSidebar, items, selectedSidebar } = props;
	const [itemsList, setItemsList] = useState(items);
	const [openKeys, setOpenKeys] = useState<string[]>([]);
	const [activeChildKey, setActiveChildKey] = useState<string>('');

	// Define parent-child relationships
	const parentChildMap: { [key: string]: string } = {
		'daily-attendance': 'time-activities-main',
		'monthly-summary': 'time-activities-main',
		'mid-month': 'run-payroll-main',
		'loan-request': 'run-payroll-main',
		'run-payroll': 'run-payroll-main',
		'calculate-statutory': 'run-payroll-main',
		'payroll-overview': 'run-payroll-main',
		payments: 'run-payroll-main',
	};

	useEffect(() => {
		const currentPath = window.location.pathname.substring(1);

		if (parentChildMap[currentPath]) {
			setActiveChildKey(currentPath);

			const parentKey = parentChildMap[currentPath];
			if (parentKey) {
				setOpenKeys((prevKeys) => {
					const otherParents = [
						'time-activities-main',
						'run-payroll-main',
					].filter((key) => key !== parentKey);
					const filtered = prevKeys.filter(
						(key) => !otherParents.includes(key)
					);
					return filtered.includes(parentKey)
						? filtered
						: [...filtered, parentKey];
				});
			}
		} else {
			setActiveChildKey('');
		}
	}, [selectedSidebar]);

	// Smooth scroll to active item
	useEffect(() => {
		const timer = setTimeout(() => {
			const activeItem = document.querySelector(
				'.ant-menu-item-selected'
			);
			if (activeItem) {
				activeItem.scrollIntoView({
					behavior: 'smooth',
					block: 'nearest',
				});
			}
		}, 100);

		return () => clearTimeout(timer);
	}, [selectedSidebar, activeChildKey]);

	useEffect(() => {
		const updatedList = items?.map((item: any) => {
			// Handle parent items
			if (item.icon) {
				if (item.key === selectedSidebar) {
					return {
						...item,
						icon: getSelectedKeyWiseIcons[
							item.key as keyof typeof getSelectedKeyWiseIcons
						],
						children: item.children?.map((child: any) => ({
							...child,
							className:
								activeChildKey === child.key
									? 'active-child-item'
									: '',
						})),
					};
				}
				return {
					...item,
					icon: getKeyWiseIcons[
						item.key as keyof typeof getKeyWiseIcons
					],
					children: item.children?.map((child: any) => ({
						...child,
						className:
							activeChildKey === child.key
								? 'active-child-item'
								: '',
					})),
				};
			} else {
				return {
					...item,
					children: item.children?.map((child: any) => ({
						...child,
						className:
							activeChildKey === child.key
								? 'active-child-item'
								: '',
					})),
				};
			}
		});
		
		setItemsList(updatedList);
	}, [selectedSidebar, items, activeChildKey]);

	// Handle submenu open/close
	const handleOpenChange = (keys: string[]) => {
		if (keys.length > 1) {
			const latestKey = keys[keys.length - 1];
			setOpenKeys([latestKey]);
		} else {
			setOpenKeys(keys);
		}
	};

	// Handle menu item click
	const handleMenuClick = (info: any) => {
		
		const clickedKey = info.key;
		

		// Check if clicked item is a top-level parent
		const isParentItem = [
			'time-activities-main',
			'run-payroll-main',
		].includes(clickedKey);

		// If clicking on a different parent item, close other parent items
		if (isParentItem) {
			const otherParents = [
				'time-activities-main',
				'run-payroll-main',
			].filter((key) => key !== clickedKey);
			const newOpenKeys = openKeys.filter(
				(key) => !otherParents.includes(key)
			);

			// Toggle current parent
			if (newOpenKeys.includes(clickedKey)) {
				setOpenKeys(newOpenKeys.filter((key) => key !== clickedKey));
			} else {
				setOpenKeys([...newOpenKeys, clickedKey]);
			}
		} else {
			// If clicking on a child item, find its parent and keep it open
			const parentKey = parentChildMap[clickedKey];
			if (parentKey) {
				const otherParents = [
					'time-activities-main',
					'run-payroll-main',
				].filter((key) => key !== parentKey);
				const newOpenKeys = openKeys.filter(
					(key) => !otherParents.includes(key)
				);

				// Keep the parent of clicked child open
				if (!newOpenKeys.includes(parentKey)) {
					newOpenKeys.push(parentKey);
				}
				setOpenKeys(newOpenKeys);

				// Set the active child
				setActiveChildKey(clickedKey);
			} else {
				// If it's a regular menu item (not parent or child), close all parents
				setOpenKeys([]);
				setActiveChildKey('');
			}
		}

		// Call the original handler
		handleSidebar(info);
	};

	// Add custom class to submenu container when it has active child
	const getSubmenuClassName = (submenuKey: string) => {
		const hasActiveChild = Object.entries(parentChildMap).some(
			([childKey, parentKey]) =>
				parentKey === submenuKey && activeChildKey === childKey
		);
		return hasActiveChild ? 'active-submenu' : '';
	};

	return (
		<Sider
			style={{
				background: '#fff',
				maxHeight: '100%',
				height: '100%',
				overflow: 'auto',
			}}
			className={styles.sidebar}
		>
			<div className={styles.sidebar__wrapper}>
				<Menu
					mode="inline"
					selectedKeys={[selectedSidebar]}
					defaultSelectedKeys={[selectedSidebar]}
					openKeys={openKeys}
					onOpenChange={handleOpenChange}
					items={itemsList?.map((item: any) => ({
						...item,
						className: item.children
							? getSubmenuClassName(item.key)
							: item.className,
					}))}
					onClick={handleMenuClick}
					className="menu-item"
					style={{
						background: '#fff',
						padding: 0,
					}}
					expandIcon={({ isOpen }) => (
						<span
							style={{
								marginRight: '25px',
							}}
						>
							{isOpen ? <UpOutlined /> : <DownOutlined />}
						</span>
					)}
				/>
			</div>
		</Sider>
	);
};

export default Sidebar;
